import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import DataConnector from 'connect/DataConnector';
import {AllPolymerLayouts} from 'constants/MapsConstants';
import {finishEditingNucleotideIndex} from 'molbio/monomers/NucleotidesUIReduxActions';
import getTestProps from 'test/lib/cypress/getTestProps';

import ComponentWrapper from './ComponentWrapper';
import {widthBetweenLetters} from './LinearMapUtil';

interface NucleotidePositionHighlightProps {
  xFn: (index: number) => number;
  sequenceId: string;
  sliceStart: number;
  sliceEnd: number;
  layout: AllPolymerLayouts['nucleotidesPositionHighlight'];
  // The height is the distance from the top of the slice to just below the DNA, which is the same height as
  // the region that is highlighted during a selection.
  height: number;
}

interface EnhancedProps {
  editingNucleotideIndices: Array<number>;
  onCompleteEditAnimation: (nucleotideIdx: number) => void;
}

class NucleotidePositionHighlightConnector extends DataConnector<
  NucleotidePositionHighlightProps,
  EnhancedProps
> {
  enhanceProps(): EnhancedProps {
    const state = this.reduxState().ui.molbio.nucleotidesUI[this.props.sequenceId];
    return {
      editingNucleotideIndices: state?.editingNucleotideIndices ?? [],
      onCompleteEditAnimation: (nucleotideIdx: number) => {
        this.dispatch(finishEditingNucleotideIndex(this.props.sequenceId, nucleotideIdx));
      },
    };
  }
}

function NucleotidePositionHighlight({
  height,
  editingNucleotideIndices,
  layout,
  onCompleteEditAnimation,
  sliceStart,
  sliceEnd,
  xFn,
}: NucleotidePositionHighlightProps & EnhancedProps): JSX.Element {
  const sliceIndices: Array<number> = [];
  for (let i = sliceStart; i < sliceEnd; i++) {
    sliceIndices.push(i);
  }
  return (
    <svg {...getTestProps({component: 'NucleotidePositionHighlight', element: 'root'})}>
      <ComponentWrapper layout={layout} shouldStopMouseDownPropagation={false}>
        <g fill="none">
          {sliceIndices.map((i) => (
            <g key={i}>
              <rect
                onAnimationEnd={() => {
                  onCompleteEditAnimation(i);
                }}
                className={
                  editingNucleotideIndices.includes(i) ? css(styles.editingIndex) : css(styles.emptyIndex)
                }
                x={xFn(i)}
                y={0}
                width={widthBetweenLetters(xFn, sliceStart)}
                height={height}
                {...getTestProps({
                  component: 'NucleotidePositionHighlight',
                  element: editingNucleotideIndices.includes(i) ? 'editingPosition' : 'emptyPosition',
                  key: String(i),
                })}
                data-amp-safe={false}
              />
            </g>
          ))}
        </g>
      </ComponentWrapper>
    </svg>
  );
}

const fillKeyframes = {
  '0%': {
    fill: 'rgba(235, 135, 21, 0.15)',
  },
  '67%': {
    fill: 'rgba(235, 135, 21, 0.15)',
  },
  '100%': {
    fill: 'rgba(235, 135, 21, 0)',
  },
};

const styles = StyleSheet.create({
  editingIndex: {
    animationFillMode: 'forwards',
    animationName: [fillKeyframes],
    animationDuration: '3s',
  },
  emptyIndex: {
    fillOpacity: 0,
  },
});

export default NucleotidePositionHighlightConnector.connect(NucleotidePositionHighlight);
